import { useAnnotateMtoMaterialMutation } from '@shared/api';
import type { MtoMaterialPosition } from '@entities/mto';
import { apiErrorMessage, needsCheck, positionRaw } from '@entities/mto';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { Actions, Decision } from '../styles';
import { MultEditor } from './MultEditor';

/** Статус строки и решение по ней. Решения учитываются в Excel и в сверке с
 *  заявками; меняются сразу (оптимистично), ошибка — над таблицей. */
export function RowDecision({
  position,
  runId,
  canWrite,
  onError,
}: {
  position: MtoMaterialPosition;
  runId: string;
  canWrite: boolean;
  onError: (msg: string) => void;
}) {
  const [annotate, { isLoading }] = useAnnotateMtoMaterialMutation();
  const label = position.name;

  async function decide(decision: 'accepted' | 'rejected' | 'reset') {
    onError('');
    try {
      await annotate({ id: position.id, runId, decision }).unwrap();
    } catch (err) {
      onError(
        `«${label}»: ${apiErrorMessage(err) ?? 'не удалось сохранить решение, попробуйте ещё раз.'}`,
      );
    }
  }

  const raw = positionRaw(position);
  // Работа из ВОР: в сверку идёт, только если снабженец её «учёл».
  const work = !!raw.vor_work;
  const excluded = position.decision === 'rejected';
  const badge = excluded ? (
    <Badge $tone="neutral">убрано из ведомости</Badge>
  ) : work && position.decision === 'accepted' ? (
    <Badge $tone="success">учтено как материал</Badge>
  ) : work ? (
    <Badge $tone="neutral">работа — в сверку не входит</Badge>
  ) : position.decision === 'accepted' && position.needs_review ? (
    <Badge $tone="success">проверено</Badge>
  ) : needsCheck(position) ? (
    <Badge $tone="warning">проверить</Badge>
  ) : null;

  return (
    <Decision>
      {badge}
      {canWrite && raw.per_unit && !excluded && (
        <MultEditor position={position} runId={runId} onError={onError} />
      )}
      {canWrite && (
        <Actions>
          {excluded ? (
            <Button
              type="button"
              $variant="ghost"
              $size="sm"
              disabled={isLoading}
              aria-label={`Вернуть в ведомость: ${label}`}
              onClick={() => decide('reset')}
            >
              Вернуть
            </Button>
          ) : work ? (
            position.decision === 'accepted' ? (
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={isLoading}
                aria-label={`Не учитывать как материал: ${label}`}
                onClick={() => decide('reset')}
              >
                Не учитывать
              </Button>
            ) : (
              <Button
                type="button"
                $variant="secondary"
                $size="sm"
                disabled={isLoading}
                aria-label={`Учесть как материал: ${label}`}
                title="Строка называет материал, которого нет в спецификации, — учесть её в сверке с заявками"
                onClick={() => decide('accepted')}
              >
                Учесть
              </Button>
            )
          ) : (
            <>
              {needsCheck(position) && (
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  disabled={isLoading}
                  aria-label={`Строка верна: ${label}`}
                  onClick={() => decide('accepted')}
                >
                  Верно
                </Button>
              )}
              <Button
                type="button"
                $variant="ghost"
                $size="sm"
                disabled={isLoading}
                aria-label={`Убрать из ведомости: ${label}`}
                title="Убрать строку из ведомости (не попадёт в Excel и в сверку)"
                onClick={() => decide('rejected')}
              >
                Убрать
              </Button>
            </>
          )}
        </Actions>
      )}
    </Decision>
  );
}
